/**
 * @file ConfirmDialog — modal de confirmación reusable para toda
 * la app. Galaxia Pro DS: brand colors, modal-card pattern, ESC
 * + backdrop click cierran (cuando NO submitting).
 *
 * Pattern imperativo via `useConfirm()`:
 *
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: "Reactivar usuario",
 *     message: "¿Reactivar a juan@example.com? Volverá a tener acceso al sistema.",
 *     confirmLabel: "Reactivar",
 *     variant: "default",
 *   });
 *   if (!ok) return;
 *
 * Para destructive con typed-confirm (eliminar rol/user) usa
 * `<TypedNameConfirm>` que exige re-typear el identificador.
 *
 * NO renderiza nada por sí solo — requiere `<ConfirmProvider>` en
 * un ancestor (montado en app.tsx).
 */
import {
  type JSX,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { GxButton, Icon } from "../../../../../.packages/galaxia-pro";
import "./confirm-dialog.css";

// ────────────────────────────────────────────────────────────────────
// Tipos públicos
// ────────────────────────────────────────────────────────────────────

export type ConfirmVariant = "default" | "danger" | "warning";

export interface ConfirmOptions {
  title: string;
  message: ReactNode;
  /** Texto del botón confirm. Default "Confirmar". */
  confirmLabel?: string;
  /** Texto del botón cancel. Default "Cancelar". */
  cancelLabel?: string;
  /** Variante visual + tono del botón confirm. */
  variant?: ConfirmVariant;
  /** Icon de Galaxia Pro para el header (refuerza el tono). */
  icon?: "bell" | "check" | "x" | "users";
}

interface PendingConfirm {
  options: ConfirmOptions;
  resolve: (ok: boolean) => void;
}

// ────────────────────────────────────────────────────────────────────
// Context
// ────────────────────────────────────────────────────────────────────

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

/** Hook imperativo. Requiere `<ConfirmProvider>` montado en un
 *  ancestor. Lanza si se llama fuera del provider — fail-fast en dev. */
export function useConfirm(): ConfirmFn {
  const fn = useContext(ConfirmContext);
  if (!fn) {
    throw new Error("useConfirm must be used within <ConfirmProvider>");
  }
  return fn;
}

// ────────────────────────────────────────────────────────────────────
// Provider
// ────────────────────────────────────────────────────────────────────

export function ConfirmProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const [pending, setPending] = useState<PendingConfirm | null>(null);

  // Mantenemos un ref al resolver para que close puede resolver
  // sin estar en el closure del setState anterior.
  const resolverRef = useRef<((ok: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((options) => {
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
      setPending({ options, resolve });
    });
  }, []);

  const handleResolve = useCallback((ok: boolean) => {
    const resolver = resolverRef.current;
    resolverRef.current = null;
    setPending(null);
    if (resolver) resolver(ok);
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending && (
        <ConfirmDialog
          options={pending.options}
          onConfirm={() => handleResolve(true)}
          onCancel={() => handleResolve(false)}
        />
      )}
    </ConfirmContext.Provider>
  );
}

// ────────────────────────────────────────────────────────────────────
// Dialog (presentational)
// ────────────────────────────────────────────────────────────────────

interface ConfirmDialogProps {
  options: ConfirmOptions;
  onConfirm: () => void;
  onCancel: () => void;
}

function ConfirmDialog({
  options,
  onConfirm,
  onCancel,
}: ConfirmDialogProps): JSX.Element | null {
  const {
    title,
    message,
    confirmLabel = "Confirmar",
    cancelLabel = "Cancelar",
    variant = "default",
    icon,
  } = options;

  const cardRef = useRef<HTMLDivElement | null>(null);

  // ESC cierra. Lock body scroll mientras está open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
      } else if (e.key === "Enter") {
        // Enter como atajo de confirmación SOLO cuando el foco no está en un
        // control que maneja Enter por sí mismo. Antes excluía input/textarea
        // pero NO botones: con el foco en "Cancelar", Enter confirmaba igual
        // (peligroso en acciones destructivas). Ahora si hay un botón
        // enfocado dejamos que su activación nativa decida (cancel o confirm).
        const target = e.target as HTMLElement;
        const tag = target.tagName;
        if (tag !== "INPUT" && tag !== "TEXTAREA" && tag !== "BUTTON" && tag !== "A") {
          e.preventDefault();
          onConfirm();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    if (typeof document !== "undefined") {
      document.documentElement.classList.add("gx-no-scroll");
    }
    // Initial focus: el botón confirm. Mejora UX (Enter funciona).
    const t = setTimeout(() => {
      const btn = cardRef.current?.querySelector<HTMLButtonElement>(
        ".confirm-dialog__confirm-btn",
      );
      btn?.focus();
    }, 0);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (typeof document !== "undefined") {
        document.documentElement.classList.remove("gx-no-scroll");
      }
      clearTimeout(t);
    };
  }, [onCancel, onConfirm]);

  if (typeof document === "undefined") return null;

  // Icon ring color por variante (refuerzo visual del tono).
  const variantClass = `gx-confirm--${variant}`;

  return createPortal(
    <div
      className="gx-confirm-backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        ref={cardRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-desc"
        className={`gx-confirm-card ${variantClass}`}
      >
        <button
          type="button"
          className="gx-confirm-close"
          onClick={onCancel}
          aria-label="Cerrar"
        >
          <Icon name="x" size={18} />
        </button>

        <div className="gx-confirm-body">
          {icon && (
            <div className={`gx-confirm-icon-ring ${variantClass}`}>
              <Icon name={icon} size={24} />
            </div>
          )}
          <h2 id="confirm-dialog-title" className="gx-confirm-title">
            {title}
          </h2>
          <div id="confirm-dialog-desc" className="gx-confirm-message">
            {message}
          </div>
        </div>

        <div className="gx-confirm-actions">
          <GxButton type="button" variant="ghost" onClick={onCancel}>
            {cancelLabel}
          </GxButton>
          <GxButton
            type="button"
            variant={variant === "danger" ? "danger" : "accent"}
            onClick={onConfirm}
            className="gx-confirm-confirm-btn"
          >
            {confirmLabel}
          </GxButton>
        </div>
      </div>
    </div>,
    document.body,
  );
}
